import {
  Alert,
  AlertTitle,
  Box,
  Container,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { TCheckoutOrderDetails } from '../../../../core/types/CheckoutOrder'
import { TProductDelivery } from '../../../../core/types/Product'
import SendIcon from '@mui/icons-material/Send'
import SendEmailIcon from '@mui/icons-material/ForwardToInbox'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import LinkIcon from '@mui/icons-material/Link'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RepayIcon from '@mui/icons-material/PaymentsOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import DownloadingIcon from '@mui/icons-material/Downloading'
import PayIcon from '@mui/icons-material/Payment'
import React from 'react'
import OrderDetailOrder from './Tabs/OrderDetailOrder'
import OrderDetailCustomer from './Tabs/OrderDetailCustomer'
import OrderDetailValues from './Tabs/OrderDetailValues'
import OrderController from '../../../../core/controllers/OrderController'
import { IErrorResponse } from '../../../../core/types/ErrorResponse'
import OrderDetailTracking from './Tabs/OrderDetailTracking'
import ContentDialog from '../../../../components/Dialogs/ContentDialog'
import SwipeableDrawerRightDialog from '../../../../components/Dialogs/SwipeableDrawerRightDialog'
import ImageIlustrationPaymentes from '../../../../components/Images/ImageIlustrationPaymentes'
import ImageIlustrationRepay from '../../../../components/Images/ImageIlustrationRepay'
import CancelIcon from '@mui/icons-material/Cancel'
import OrderFunctions from '../../../../core/functions/OrderFunctions'
import { TabPanel, a11yProps } from '../../../../components/Tabs'
import { useDownloadPDF } from '../../../../core/hooks/useDownloadPDF'
import { ReportDownloadController } from '../../../../core/controllers/ReportDownloadController'
import { HtmlTooltip } from '../../../../components/Tooltip/HtmlTooltip'
import OrderDetailEmails from './Tabs/OrderDetailEmails'
import OrderDetailDispute from './Tabs/OrderDetailDispute'
import { useChargebackDisputeAccess } from '../../../../core/context/TenantSettingsContext'
import CommunityIntegrationController from '../../../../core/controllers/CommunityIntegrationController'
import { ICheckoutOrderDetailsProp } from '../CheckoutOrderConsts'
import CheckoutOrderResendEmailDialog from './components/CheckoutOrderResendEmailDialog'
import TicketController from '../../../../core/controllers/TicketController'
import { AuthContext } from '../../../../core/context/AuthContext'

const CheckoutOrderDetails = ({
  checkoutOrder,
  open,
  isOpened,
  handleClose,
  setOpen,
  setSuccess,
  setError,
  execute,
  setCopied,
}: ICheckoutOrderDetailsProp) => {
  const { isUserSysAdmin } = React.useContext(AuthContext)
  const [value, setValue] = React.useState(0)
  const [openRepay, setOpenRepay] = React.useState(false)
  const [openNotRepay, setOpenNotRepay] = React.useState(false)
  const [openManualPayment, setOpenManualPayment] = React.useState(false)
  const [openSubscriptionPaymentCancel, setOpenSubscriptionPaymentCancel] =
    React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState<TCheckoutOrderDetails | undefined>(
    undefined
  )
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [openTelegramInviteDialog, setOpenTelegramInviteDialog] =
    React.useState(false)
  const [telegramInviteLink, setTelegramInviteLink] = React.useState<
    string | null
  >(null)
  const [openResendEmail, setOpenResendEmail] = React.useState(false)

  const productDeliveryType =
    data?.productDeliveryType ?? data?.ProductDeliveryType
  const isTicketOrder = productDeliveryType === TProductDelivery.Ticket
  const disputeAccess = useChargebackDisputeAccess()
  const showDisputeTab =
    OrderFunctions.isChargedback(checkoutOrder) && disputeAccess === 'allowed'

  const handleResendEmail = () => {
    handleMenuClose()
    setOpenResendEmail(true)
  }

  const handleSendTicket = async () => {
    handleMenuClose()
    setSuccess(false)
    setError('')
    setLoading(true)

    try {
      const orderCode = data?.codeId || checkoutOrder?.codeId || ''
      if (!orderCode) {
        setError('Nenhuma transação selecionada')
        return
      }

      const response = await TicketController.processOrResend({ orderCode })

      if (!response.success) {
        setError(response.error)
        return
      }

      setSuccess(true)
      execute()
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadFile = () => {
    return ReportDownloadController.getOrderRefundPdf({
      id: checkoutOrder?.id ?? '',
    })
  }

  const handlerDownloadingError = (e: string) => setError(e)

  const getFileName = () =>
    `refund_${new Date().toISOString().replace(':', '_').replace('.', '_')}.pdf`

  const { download, downloading } = useDownloadPDF({
    apiDefinition: handleDownloadFile,
    onError: handlerDownloadingError,
    getFileName,
  })

  const downloadCallback = React.useCallback(() => download(), [download])

  const handleMyClose = () => {
    handleMenuClose()
    handleClose()
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleRepay = () => {
    handleMenuClose()
    if (!checkoutOrder?.inAssurance) {
      setOpenNotRepay(true)
    } else {
      setOpenRepay(true)
    }
  }

  const handleManualPayment = () => {
    handleMenuClose()
    if (OrderFunctions.isPaid(checkoutOrder)) {
      manualPayment()
    } else {
      setOpenManualPayment(true)
    }
  }

  const handleSubscriptionPaymentCancel = () => {
    handleMenuClose()
    subscriptionPaymentCancel()
  }

  const repay = async (): Promise<boolean> => {
    setSuccess(false)
    setError('')
    try {
      const orderChargeId =
        checkoutOrder?.orderChargeId || data?.orderChargeId || ''

      var response = await OrderController.deleteCharge({
        orderChargeId,
        checkoutOrderId: checkoutOrder?.id || '',
        isAdmin: isUserSysAdmin(),
      })
      if (!response.success) {
        setError(response.error)
      } else {
        setSuccess(true)
        handleClose()
        execute()
      }
    } finally {
      return true
    }
  }

  const subscriptionPaymentCancel = async () => {
    setSuccess(false)
    setError('')
    try {
      var response = await OrderController.subscriptionPaymentCancel({
        checkoutOrderId: checkoutOrder?.id || '',
      })
      if (!response.success) {
        setError(response.error)
      } else {
        setSuccess(true)
        handleClose()
        execute()
      }
    } finally {
      return true
    }
  }

  const handleRenewTelegramInviteLink = async () => {
    if (checkoutOrder && checkoutOrder.id) {
      setSuccess(false)
      setError('')

      try {
        var response =
          await CommunityIntegrationController.renewTelegramInviteLink(
            checkoutOrder?.id,
            checkoutOrder?.productPriceId
          )
        if (!response.success) {
          setError(response.error)
        }
        const newLink = response.data as string
        setTelegramInviteLink(newLink)
        setOpenTelegramInviteDialog(true)
      } finally {
        return true
      }
    }
  }

  const getDetails = React.useCallback(
    async (id: string, productPriceId?: string, orderChargeId?: string) => {
      try {
        setLoading(true)
        setError('')
        if (data) {
          setData(undefined)
        }

        const response = await OrderController.getCheckoutOrderDetails({
          id,
          productPriceId,
          orderChargeId,
        })
        var responseError = response as IErrorResponse

        if (responseError.code) {
          setError(responseError.error)
        } else {
          const responseData = response as TCheckoutOrderDetails
          setData(responseData)
        }
      } catch (e) {
        var message = 'Erro não identificado'
        if (typeof e === 'string') {
          message = e.toUpperCase()
        } else if (e instanceof Error) {
          const err = e as Error
          message = err.message
        }
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const manualPayment = async () => {
    setSuccess(false)
    setError('')
    try {
      var response = await OrderController.manualPayment({
        checkoutOrderId: checkoutOrder?.id || '',
      })
      if (!response.success) {
        setError(response.error)
      } else {
        setSuccess(true)
        handleClose()
        execute()
      }
    } finally {
      return true
    }
  }

  React.useEffect(() => {
    if (checkoutOrder && checkoutOrder.id) {
      setValue(0)
      getDetails(
        checkoutOrder.id,
        checkoutOrder.productPriceId,
        checkoutOrder.orderChargeId || undefined
      )
    }
  }, [checkoutOrder])

  return (
    <SwipeableDrawerRightDialog
      hideButtons
      open={open}
      onClose={handleMyClose}
      setOpen={setOpen}
      backgroundColor="#fafafa"
      title="Detalhes da transação"
      subtitle="Visualize os detalhes da transação realizada"
    >
      <>
        <Stack direction="column" sx={{ width: '100%' }}>
          <Box>
            <Stack
              direction="row"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              alignItems="center"
            >
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flexGrow: 1 }}
              >
                <Tab label="Transação" {...a11yProps(0)} />
                <Tab label="Cliente" {...a11yProps(1)} />
                <Tab label="Valores" {...a11yProps(2)} />
                <Tab label="Track" {...a11yProps(3)} />
                <Tab label="E-mails" {...a11yProps(4)} />
                {showDisputeTab && (
                  <Tab label="Contestação" {...a11yProps(5)} />
                )}
                {/*OrderFunctions.isSubscription(checkoutOrder) && (
                  <Tab label="Pagamentos" {...a11yProps(4)} />
                )*/}
              </Tabs>
              {OrderFunctions.isPaid(checkoutOrder) && (
                <div>
                  <IconButton
                    size="medium"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    {/* Produtor só reembolsa em refund_claimed (backend recusa o resto) */}
                    {isUserSysAdmin() && (
                      <HtmlTooltip
                        title={
                          !!checkoutOrder?.reconciliationId
                            ? 'Entre em contato com o produtor para o reembolso'
                            : ''
                        }
                      >
                        <Box>
                          <MenuItem
                            onClick={handleRepay}
                            disabled={!!checkoutOrder?.reconciliationId}
                          >
                            <ListItemIcon>
                              <RepayIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Reembolsar transação</ListItemText>
                          </MenuItem>
                        </Box>
                      </HtmlTooltip>
                    )}

                    <MenuItem onClick={handleManualPayment}>
                      <ListItemIcon>
                        <SendIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Reenviar eventos</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={handleResendEmail}>
                      <ListItemIcon>
                        <SendEmailIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Reenviar e-mail</ListItemText>
                    </MenuItem>

                    {isTicketOrder && (
                      <MenuItem onClick={handleSendTicket}>
                        <ListItemIcon>
                          <ConfirmationNumberOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Enviar ingresso</ListItemText>
                      </MenuItem>
                    )}

                    {OrderFunctions.isSubscriptionAndNotPaymentCancel(
                      checkoutOrder,
                      data
                    ) && (
                      <MenuItem onClick={handleSubscriptionPaymentCancel}>
                        <ListItemIcon>
                          <CancelIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          Cancelar cobrança de assinatura
                        </ListItemText>
                      </MenuItem>
                    )}

                    {data?.isTelegramPrivateGroup && (
                      <MenuItem onClick={handleRenewTelegramInviteLink}>
                        <ListItemIcon>
                          <LinkIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Renovar link de convite</ListItemText>
                      </MenuItem>
                    )}
                  </Menu>
                </div>
              )}
              {OrderFunctions.isRefundClaim(checkoutOrder) && (
                <div>
                  <IconButton
                    size="medium"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={handleRepay}>
                      <ListItemIcon>
                        <RepayIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Reembolsar transação</ListItemText>
                    </MenuItem>
                  </Menu>
                </div>
              )}
              {OrderFunctions.isCanceled(checkoutOrder) && (
                <div>
                  <IconButton
                    size="medium"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={downloadCallback} disabled={downloading}>
                      {!downloading && (
                        <ListItemIcon>
                          <DownloadIcon fontSize="small" />
                        </ListItemIcon>
                      )}
                      {!downloading && (
                        <ListItemText>
                          Baixar comprovante de cancelamento
                        </ListItemText>
                      )}
                      {downloading && (
                        <ListItemIcon>
                          <DownloadingIcon fontSize="small" />
                        </ListItemIcon>
                      )}
                      {downloading && (
                        <ListItemText>Baixando o comprovante...</ListItemText>
                      )}
                    </MenuItem>
                  </Menu>
                </div>
              )}
              {OrderFunctions.isPendings(checkoutOrder) && (
                <div>
                  <IconButton
                    size="medium"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    color="inherit"
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={handleManualPayment}>
                      <ListItemIcon>
                        <PayIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Aprovar pagamento</ListItemText>
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </Stack>
            <TabPanel value={value} index={0}>
              <OrderDetailOrder
                checkoutOrder={checkoutOrder}
                details={data}
                isOpened={isOpened}
                loading={loading}
                setSending={setLoading}
                setSuccess={setSuccess}
                setError={setError}
                execute={execute}
                getDetails={getDetails}
                setCopied={setCopied}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <OrderDetailCustomer
                checkoutOrder={checkoutOrder}
                details={data}
                loading={loading}
              />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <OrderDetailValues
                checkoutOrder={checkoutOrder}
                details={data}
                loading={loading}
              />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <OrderDetailTracking
                checkoutOrder={checkoutOrder}
                details={data}
                loading={loading}
                setSending={setLoading}
                setSuccess={setSuccess}
                setError={setError}
              />
            </TabPanel>
            <TabPanel value={value} index={4}>
              <OrderDetailEmails
                checkoutOrder={checkoutOrder}
                details={data}
                loading={loading}
              />
            </TabPanel>
            {showDisputeTab && (
              <TabPanel value={value} index={5}>
                <OrderDetailDispute
                  orderChargeId={
                    checkoutOrder?.orderChargeId || data?.orderChargeId
                  }
                />
              </TabPanel>
            )}
            {/*OrderFunctions.isSubscription(checkoutOrder) && (
              <TabPanel value={value} index={4}>
                <OrderDetailPayments
                  checkoutOrder={checkoutOrder}
                  details={data}
                />
              </TabPanel>
            )*/}
          </Box>
        </Stack>

        <ContentDialog
          open={openRepay}
          onClose={() => setOpenRepay(false)}
          title="Como funciona o reembolso"
          labelPrositive="Reembolsar"
          onPositive={repay}
        >
          <Container>
            <ImageIlustrationRepay />
            <Typography>
              Após a solicitação do estorno, a solicitação é enviada a operadora
              do cartão do cliente, que é o responsável pela devolução do saldo
              no cartão dele.
            </Typography>
            <Typography>
              Assim, o cliente pode receber esse valor de 3 formas de acordo com
              a operadora/banco emissor:
            </Typography>
            <ul>
              <li>
                <Typography>
                  O banco estorna o valor total da transação na fatura seguinte
                  à solicitação do estorno; liberando o limite do cartão do
                  cliente e cobrando o restante das parcelas normalmente.
                </Typography>
              </li>
              <li>
                <Typography>
                  O banco cobra todas as parcelas e faz um estorno total apenas
                  na última fatura da cobrança
                </Typography>
              </li>
              <li>
                <Typography>
                  O banco faz a cobrança e o estorno da parcela na mesma fatura.
                  Em casos de estorno parcial, o banco pode dividir o valor do
                  estorno pelo restante das transações – nesse caso é cobrada
                  uma parcela original e devolvida uma parcela de estorno na
                  mesma fatura.
                </Typography>
              </li>
            </ul>
          </Container>
        </ContentDialog>

        <ContentDialog
          open={openNotRepay}
          onClose={() => setOpenNotRepay(false)}
          title="Operação inválida"
          hasCancel={false}
        >
          <Typography>
            Não é permitido o reembolso após o período de garantia.
          </Typography>
          <Alert
            variant="standard"
            severity="info"
            sx={{ width: '100%', mt: 3 }}
          >
            Só é permitido o reembolso enquanto o produto estiver no período de
            garantia
          </Alert>
        </ContentDialog>

        <ContentDialog
          open={openManualPayment}
          onClose={() => setOpenManualPayment(false)}
          title="Como funciona a aprovação do pagamento"
          labelPrositive="Aprovar pagamento"
          onPositive={manualPayment}
        >
          <Container>
            <ImageIlustrationPaymentes />
            <Typography>
              Ideal para pagamentos do tipo boleto em que o cliente está com o
              comprovante em mãos e não precisa esperar que a plataforma
              identifique o pagamento automaticamente.
            </Typography>
            <br />
            <Typography>
              Após a aprovação do pagamento, a venda é identificada como paga e
              será disparado os eventos conforme configurações.
            </Typography>
            <Alert severity="info" variant="standard" sx={{ my: 2 }}>
              Liberação imediata para o acesso a área de membros.
            </Alert>
            <Alert severity="warning" variant="standard" sx={{ my: 2 }}>
              <AlertTitle>Alerta</AlertTitle>
              Após esta ação, só será permitido realizar o reembolso como
              cancelamento da compra.
            </Alert>
          </Container>
        </ContentDialog>

        <ContentDialog
          open={openSubscriptionPaymentCancel}
          onClose={() => setOpenSubscriptionPaymentCancel(false)}
          title="Cancelar assinatura"
          labelPrositive="Aprovar cancelamento de assinatura"
          onPositive={subscriptionPaymentCancel}
        >
          <Typography>
            {' '}
            Ao cancelar a cobrança de assinatura, a assinatura expirará após a
            data de renovação, e o cliente não será cobrado no futuro.{' '}
          </Typography>
        </ContentDialog>

        <ContentDialog
          open={openTelegramInviteDialog}
          onClose={() => setOpenTelegramInviteDialog(false)}
          title="Link de convite renovado"
          labelPrositive="Copiar link"
          onPositive={async () => {
            if (telegramInviteLink) {
              try {
                await navigator.clipboard.writeText(telegramInviteLink)
                setCopied(true) // você já recebe isso por prop
              } catch (err) {
                setError(
                  'Não foi possível copiar o link para a área de transferência.'
                )
              }
            }

            setOpenTelegramInviteDialog(false)
            return true
          }}
        >
          <Container>
            <Typography sx={{ mb: 2 }}>
              O link de convite do grupo no Telegram foi renovado com sucesso.
              Use o link abaixo:
            </Typography>

            <Alert
              variant="outlined"
              severity="info"
              sx={{ wordBreak: 'break-all', fontSize: '0.9rem' }}
            >
              {telegramInviteLink}
            </Alert>

            <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
              Clique em &quot;Copiar link&quot; para enviar esse link ao cliente
              ou utilizar em sua comunicação.
            </Typography>
          </Container>
        </ContentDialog>

        <CheckoutOrderResendEmailDialog
          open={openResendEmail}
          setOpen={setOpenResendEmail}
          checkoutOrder={checkoutOrder}
          execute={execute}
        />
      </>
    </SwipeableDrawerRightDialog>
  )
}

export default CheckoutOrderDetails
